import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Box,
  Button,
  Container,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Menu,
  Close,
  Facebook,
  Instagram,
  LinkedIn,
} from "@mui/icons-material";
import logo from "../../assets/NC-Logo.png";
import { Link, NavLink } from "react-router-dom";

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isTablet = useMediaQuery(theme.breakpoints.down("lg"));
  const drawerRef = useRef(null);
  const menuButtonRef = useRef(null);

  // Затваряне на менюто при клик извън него
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        mobileMenuOpen &&
        drawerRef.current &&
        !drawerRef.current.contains(event.target) &&
        menuButtonRef.current &&
        !menuButtonRef.current.contains(event.target)
      ) {
        setMobileMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [mobileMenuOpen]);

  const menuItems = [
    { title: "НАЧАЛО", href: "/", refresh: true },
    { title: "ЗА ПРОЕКТА", href: "/about", refresh: false },
    { title: "МЕТОДОЛОГИЯ", href: "/methodology", refresh: false },
    { title: "ЧИТАЛИЩА", href: "/community-center", refresh: false },
    { title: "ЧЗВ", href: "/faq", refresh: false },
    { title: "ОБРАТНА ВРЪЗКА", href: "/feedback", refresh: false },
  ];

  const externalNavItems = [
    {
      label: "ОСНОВЕН САЙТ",
      url: "https://narodnichitalishta.bg/",
      icon: "📊",
    },
    { label: "ДАННИ", url: "https://danni.narodnichitalishta.bg/", icon: "🗺️" },
    {
      label: "НАРЪЧНИК",
      url: "https://naruchnik.narodnichitalishta.bg/",
      icon: "📘",
    },
    {
      label: "ГРАНТОВЕ",
      url: "https://grantove.narodnichitalishta.bg/",
      icon: "💰",
    },
  ];

  // Затваряне на менюто при смяна на руут
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [window.location.pathname]);

  return (
    <>
      <AppBar
        position="fixed"
        sx={{
          background: theme.palette.custom.secondaryGradient,
          backdropFilter: "blur(8px)",
          boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.1)",
          zIndex: 1300,
          // top: { xs: 0, md: "28px" },
        }}
      >
        {/* Нов компонент за външни навигационни връзки */}
        <Box
          sx={{
            background: theme.palette.background.default,
            py: 0.5,
            borderBottom: `1px solid ${theme.palette.grey[300]}`,
            display: { xs: "none", md: "block" },
            position: "relative",
            zIndex: 1400,
          }}
        >
          <Container maxWidth="xl" disableGutters>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                overflowX: "auto",
                px: { xs: 2, sm: 3, md: 4 },
                "&::-webkit-scrollbar": {
                  display: "none",
                },
                scrollbarWidth: "none",
              }}
            >
              {externalNavItems.map((item) => (
                <Button
                  key={item.label}
                  component="a"
                  href={item.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    color: theme.palette.text.grayLight,
                    textTransform: "none",
                    fontSize: {
                      xs: "0.7rem",
                      sm: "0.75rem",
                      md: "0.8rem",
                      lg: "0.85rem",
                    },
                    fontWeight: 600,
                    py: 0,
                    px: 1,
                    minWidth: "auto",
                    whiteSpace: "nowrap",
                    display: "flex",
                    alignItems: "center",
                    // gap: 0.5,
                    position: "relative",
                    overflow: "visible",
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",

                    "&:hover": {
                      color: theme.palette.custom.primaryOrange,
                      backgroundColor: "transparent",
                      transform: "translateY(-2px)",
                    },

                    "&::after": {
                      content: '""',
                      position: "absolute",
                      bottom: "-2px",
                      left: "50%",
                      width: "0%",
                      height: "1px",
                      backgroundColor: theme.palette.custom.primaryOrange,
                      transform: "translateX(-50%)",
                      transition: "width 0.4s cubic-bezier(0.25, 1, 0.5, 1)",
                      opacity: 1,
                    },

                    "&:hover::after": {
                      width: "100%",
                    },

                    "&:focus-visible": {
                      outline: `1px solid ${theme.palette.custom.primaryOrange}`,
                      outlineOffset: "3px",
                      color: theme.palette.custom.primaryOrange,
                    },

                    "&:active": {
                      transform: "translateY(0)",
                      transition: "transform 0.1s",
                    },
                  }}
                >
                  {item.label}
                </Button>
              ))}
            </Box>
          </Container>
        </Box>

        <Container
          maxWidth="xl"
          sx={{
            px: { xs: 2, sm: 3, md: 4 },
            bgcolor: 'transparent',
          }}
        >
          <Toolbar
            sx={{
              minHeight: { xs: "70px", md: "80px" },
              py: { xs: 1, md: 1 },
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            {/* Лого */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              style={{
                display: "flex",
                alignItems: "center",
              }}
            >
              <a
                href="/"
                style={{
                  display: "flex",
                  alignItems: "center",
                  textDecoration: "none",
                }}
              >
                <div
                  style={{
                    height: isMobile ? "40px" : "50px",
                    width: "auto",
                    maxWidth: isMobile ? "120px" : "180px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                  }}
                >
                  <img
                    src={logo}
                    alt="Народни Читалища Лого"
                    style={{
                      height: "100%",
                      width: "auto",
                      objectFit: "contain",
                      maxHeight: "100%",
                    }}
                  />
                </div>
              </a>
            </motion.div>

            {/* Desktop Navigation */}
            <Box
              sx={{
                display: { xs: "none", md: "flex" },
                alignItems: "center",
                gap: 2,
              }}
            >
              {menuItems.map((item, index) => (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  {item.refresh ? (
                    <a
                      href={item.href}
                      style={{
                        color: theme.palette.text.primary,
                        textDecoration: "none",
                        fontWeight: 600,
                        fontSize: "1rem",
                        padding: "8px 12px",
                        transition: "all 0.2s ease-in-out",
                        borderBottom:
                          window.location.pathname === item.href
                            ? `2px solid ${theme.palette.primary.main}`
                            : "2px solid transparent",
                        "&:hover": {
                          color: theme.palette.primary.main,
                          borderBottom: `2px solid ${theme.palette.primary.main}`,
                        },
                      }}
                    >
                      {item.title}
                    </a>
                  ) : (
                    <NavLink
                      to={item.href}
                      style={({ isActive }) => ({
                        color: isActive
                          ? theme.palette.primary.main
                          : theme.palette.text.primary,
                        textDecoration: "none",
                        fontWeight: 600,
                        fontSize: "1rem",
                        padding: "8px 12px",
                        transition: "all 0.2s ease-in-out",
                        borderBottom: isActive
                          ? `2px solid ${theme.palette.primary.main}`
                          : "2px solid transparent",
                        "&:hover": {
                          color: theme.palette.primary.main,
                          borderBottom: `2px solid ${theme.palette.primary.main}`,
                        },
                      })}
                    >
                      {item.title}
                    </NavLink>
                  )}
                </motion.div>
              ))}
            </Box>

            {/* Mobile Menu Button */}
            {isMobile && (
              <IconButton
                ref={menuButtonRef}
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                sx={{
                  color: theme.palette.text.primary,
                  padding: "8px",
                  marginLeft: "auto",
                  minWidth: "40px",
                  minHeight: "40px",
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    backgroundColor: "rgba(250, 126, 40, 0.1)",
                    color: theme.palette.custom.primaryOrange,
                  },
                }}
                size="medium"
              >
                {mobileMenuOpen ? (
                  <Close fontSize="medium" />
                ) : (
                  <Menu fontSize="medium" />
                )}
              </IconButton>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Menu Drawer */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <div className="fixed inset-0 z-40 md:hidden">
            {/* Overlay */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black bg-opacity-50"
              onClick={() => setMobileMenuOpen(false)}
            />

            {/* Drawer */}
            <motion.div
              ref={drawerRef}
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "tween", duration: 0.3 }}
              className="absolute right-0 top-0 h-full w-72 bg-white shadow-2xl"
            >
              <div className="h-full flex flex-col">
                {/* Header */}
                <div className="flex justify-between items-center p-4 border-b border-gray-200">
                  <div className="flex items-center space-x-3">
                    <div className="h-10 w-10 rounded-lg overflow-hidden border border-gray-200 flex items-center justify-center">
                      <img
                        src={logo}
                        alt="Народни Читалища Лого"
                        className="h-full w-auto object-contain"
                      />
                    </div>
                    <Typography
                      variant="h6"
                      className="font-bold text-gray-800 text-lg"
                    >
                      Меню
                    </Typography>
                  </div>
                  <IconButton
                    onClick={() => setMobileMenuOpen(false)}
                    sx={{
                      color: theme.palette.text.primary,
                      "&:hover": {
                        backgroundColor: "rgba(250, 126, 40, 0.1)",
                        color: theme.palette.custom.primaryOrange,
                      },
                    }}
                    size="medium"
                  >
                    <Close />
                  </IconButton>
                </div>

                {/* Mobile Menu Items */}
                <List className="flex-1 p-4 overflow-y-auto">
                  {menuItems.map((item, index) => (
                    <motion.div
                      key={item.title}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                    >
                      <ListItem
                        component={item.refresh ? "a" : Link}
                        href={item.refresh ? item.href : undefined}
                        to={!item.refresh ? item.href : undefined}
                        onClick={() => setMobileMenuOpen(false)}
                        className="rounded-lg mb-2 hover:bg-gray-100 active:bg-gray-200 transition-colors duration-200 py-3"
                        sx={{ minHeight: "50px" }}
                      >
                        <ListItemText
                          primary={item.title}
                          primaryTypographyProps={{
                            className: "font-semibold text-gray-800 text-base",
                          }}
                        />
                      </ListItem>
                    </motion.div>
                  ))}
                </List>

                {/* External Navigation Items in Mobile Menu */}
                <Box className="p-4 border-t border-gray-200">
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: theme.palette.text.grayLight,
                      fontWeight: 600,
                      mb: 2,
                      textAlign: "center",
                    }}
                  >
                    ВЪНШНИ ВРЪЗКИ
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                    }}
                  >
                    {externalNavItems.map((item) => (
                      <Button
                        key={item.label}
                        component="a"
                        href={item.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => setMobileMenuOpen(false)}
                        sx={{
                          color: theme.palette.text.grayLight,
                          textTransform: "none",
                          fontSize: "0.8rem",
                          fontWeight: 600,
                          py: 0.5,
                          px: 2,
                          justifyContent: "flex-start",
                          transition: "all 0.2s ease-in-out",
                          "&:hover": {
                            color: theme.palette.custom.primaryOrange,
                            backgroundColor: "rgba(250, 126, 40, 0.05)",
                          },
                        }}
                      >
                        {/* <span style={{ marginRight: 8 }}>{item.icon}</span> */}
                        {item.label}
                      </Button>
                    ))}
                  </Box>
                </Box>

                {/* Social Icons in Mobile Menu */}
                {/* <div className="p-4 border-t border-gray-200">
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: theme.palette.text.grayLight,
                      fontWeight: 600,
                      mb: 3,
                      textAlign: "center",
                    }}
                  >
                    ПОСЛЕДВАЙТЕ НИ
                  </Typography>
                  <div className="flex justify-center space-x-3">
                    <IconButton
                      size="medium"
                      sx={{
                        color: theme.palette.text.grayLight,
                        transition: "all 0.2s ease-in-out",
                        "&:hover": {
                          color: theme.palette.custom.primaryOrange,
                          backgroundColor: "rgba(250, 126, 40, 0.1)",
                        },
                      }}
                      href="https://www.facebook.com/narodnichitalishta/?locale=bg_BG"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <Facebook />
                    </IconButton>
                    <IconButton
                      size="medium"
                      sx={{
                        color: theme.palette.text.grayLight,
                        transition: "all 0.2s ease-in-out",
                        "&:hover": {
                          color: theme.palette.custom.primaryOrange,
                          backgroundColor: "rgba(250, 126, 40, 0.1)",
                        },
                      }}
                      href="https://www.instagram.com/narodnichitalishta/#"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <Instagram />
                    </IconButton>
                    <IconButton
                      size="medium"
                      sx={{
                        color: theme.palette.text.grayLight,
                        transition: "all 0.2s ease-in-out",
                        "&:hover": {
                          color: theme.palette.custom.primaryOrange,
                          backgroundColor: "rgba(250, 126, 40, 0.1)",
                        },
                      }}
                      href="https://www.linkedin.com/company/narodni-chitalishta-foundation/"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <LinkedIn />
                    </IconButton>
                  </div>
                </div> */}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Spacer for fixed header */}
      <Box
        sx={{
          height: {
            xs: "70px", // Само за основния хедър на мобилни
            md: "110px", // За двата хедъра на десктоп
          },
        }}
      />
    </>
  );
};

export default Header;
